import { FC, memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import styled from 'styled-components';

import { AUTH_ROUTES } from '@navigation/types';
import { useModal } from '@hooks/useModal';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useAlert } from '@providers/AlertProvider';
import { useAuthUser } from '@redux/selectors/auth';
import { useAppDispatch, logoutAction } from '@redux/store';
import {
  FAQ_URL,
  PRIVACY_POLICY_URL,
  TERMS_OR_SERVICE_URL,
  AlertType,
  SKEW_ANGLE,
  MOBILE_SKEW_ANGLE,
} from '@utils/constants';
import Modal from '@components/modal';
import { PremierText } from '@components/text/Text';
import { useScreenSizes } from '@hooks/useScreenSizes';

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
`;

const SidebarContainer = styled.div<{ $collapsed?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  transition: width 0.3s ease;
`;

const Nav = styled.nav`
  flex: 1;
  margin-top: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`;

const NavItem = styled(Link)`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  &:hover {
    background-color: ${({ theme }) => theme.colors.black};
  }
  &.active {
    border-image: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.red[500]},
        ${({ theme }) => theme.colors.purple[500]},
        ${({ theme }) => theme.colors.blue[500]}
      )
      1;
  }
`;

const Text = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xl};
`;

const LogoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: auto;
  margin-bottom: 16px;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-bottom: 0;
    padding-bottom: max(16px, env(safe-area-inset-bottom));
  }
`;

const LogoutButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
  padding: 8px;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
`;

const ModalText = styled(PremierText)`
  color: ${({ theme }) => theme.colors.white};
  transform: skewX(${SKEW_ANGLE}deg);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    transform: skewX(${MOBILE_SKEW_ANGLE}deg);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.black};
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  gap: 8px;

  & > a:first-child {
    position: relative;
    padding-right: 8px;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 16px;
      background: linear-gradient(
        to bottom,
        ${({ theme }) => theme.colors.red[500]},
        ${({ theme }) => theme.colors.purple[500]},
        ${({ theme }) => theme.colors.blue[500]}
      );
    }
  }
`;

const LinkText = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[400]};
`;

const TermsOfServiceLink = styled(Link)`
  text-decoration: none;
`;

const PrivacyPolicyLink = styled(Link)`
  text-decoration: none;
`;

const menuItems = [
  {
    label: 'Home',
    path: '/',
    name: 'home',
    showInMobile: false,
  },
  {
    label: 'Channels',
    path: '/channels',
    name: 'channels',
    showInMobile: true,
  },
  // {
  //   label: 'My Subscriptions',
  //   path: '/subscriptions',
  //   name: 'subscriptions',
  // },
  {
    label: 'People You Follow',
    path: '/following',
    name: 'following',
    showInMobile: true,
  },
  {
    label: 'Inbox',
    path: '/inbox',
    name: 'inbox',
    showInMobile: false,
  },
  // {
  //   label: 'Football Scores',
  //   path: '/football-scores',
  //   name: 'football-scores',
  // },
  {
    label: 'Verify Account',
    path: '/verify-account',
    name: 'verify-account',
    showInMobile: true,
  },
  {
    label: 'FAQ',
    path: FAQ_URL,
    name: 'faq',
    showInMobile: true,
  },
];

type Props = {
  children?: ReactNode;
  isCollapsed: boolean;
  onClose?: () => void;
};

const Sidebar: FC<Props> = ({ children, isCollapsed, onClose }) => {
  const [activeItem, setActiveItem] = useState<string>('home');
  const { showAlert } = useAlert();
  const { isModalShown, hideModal, showModal } = useModal();
  const { openDownloadAppModal } = useDownloadAppModal();
  const dispatch = useAppDispatch();
  const { isAnonymous } = useAuthUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet } = useScreenSizes();

  const onNavItemClick = useCallback(
    (name: string) => {
      switch (name) {
        case 'faq':
          return;
        case 'channels':
        case 'following':
        case 'verify-account':
        case 'inbox':
          return openDownloadAppModal();
        default:
          break;
      }

      onClose?.();
    },
    [openDownloadAppModal, onClose],
  );

  useEffect(() => {
    const path = location.pathname.split('/')[1] || 'home';

    if (path !== activeItem) {
      setActiveItem(path);
    }
  }, [location.pathname, activeItem]);

  const onLogout = useCallback(() => {
    dispatch(logoutAction());
    hideModal();
    onClose?.();
    navigate(AUTH_ROUTES.LOGIN);
    showAlert(AlertType.Success, 'Successfully logged out!');
  }, [dispatch, hideModal, navigate, showAlert, onClose]);

  return (
    <Container>
      <SidebarContainer $collapsed={isCollapsed}>
        <Nav>
          {menuItems.map((item) =>
            (isMobile || isTablet) && !item.showInMobile ? null : (
              <NavItem
                key={item.name}
                className={activeItem === item.name ? 'active' : ''}
                onClick={() => onNavItemClick(item.name)}
                to={
                  item.name === 'verify-account' ||
                  item.name === 'following' ||
                  item.name === 'channels' ||
                  item.name === 'inbox'
                    ? '#'
                    : item.path
                }
                target={item.path === FAQ_URL ? '_blank' : undefined}
                rel={item.path === FAQ_URL ? 'noopener noreferrer' : undefined}
              >
                <Text>{item.label}</Text>
              </NavItem>
            ),
          )}
        </Nav>

        <LogoutContainer>
          {!isAnonymous && (
            <LogoutButton onClick={() => showModal()}>
              <Text>Logout</Text>
            </LogoutButton>
          )}

          <LinksContainer>
            <TermsOfServiceLink
              to={TERMS_OR_SERVICE_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              <LinkText> Terms of Service</LinkText>
            </TermsOfServiceLink>

            <PrivacyPolicyLink
              to={PRIVACY_POLICY_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              <LinkText>Privacy Policy</LinkText>
            </PrivacyPolicyLink>
          </LinksContainer>
        </LogoutContainer>
      </SidebarContainer>

      {children && <ContentArea>{children}</ContentArea>}

      <Modal
        isShown={isModalShown}
        onClose={hideModal}
        title='Logout'
        onConfirm={onLogout}
      >
        <ModalText>Are you sure you want to logout?</ModalText>
      </Modal>
    </Container>
  );
};

export default memo(Sidebar);
